import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tokenService } from '../services/tokenService';
import type { CreateTokenRequest, UpdateTokenCodesRequest } from '../types';

// Query keys
export const tokenKeys = {
  all: ['tokens'] as const,
  lists: () => [...tokenKeys.all, 'list'] as const,
  list: (filters?: any) => [...tokenKeys.lists(), filters] as const,
  details: () => [...tokenKeys.all, 'detail'] as const,
  detail: (tokenId: string) => [...tokenKeys.details(), tokenId] as const,
};

/**
 * Hook to fetch all tokens for the current user
 */
export const useTokens = () => {
  return useQuery({
    queryKey: tokenKeys.list(),
    queryFn: () => tokenService.getUserTokens(),
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchInterval: 60000, // Refetch every minute to keep token status updated
  });
};

/**
 * Hook to fetch a specific token with full details including access token
 */
export const useTokenDetails = (tokenId: string, enabled = false) => {
  return useQuery({
    queryKey: tokenKeys.detail(tokenId),
    queryFn: () => tokenService.getToken(tokenId),
    enabled,
    staleTime: 0, // Never cache token details for security
    gcTime: 0, // Don't cache in memory either
  });
};

/**
 * Hook to create a new token
 */
export const useCreateToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTokenRequest) => tokenService.createToken(data),
    onSuccess: () => {
      // Invalidate token list to refetch
      queryClient.invalidateQueries({ queryKey: tokenKeys.lists() });
    },
  });
};

/**
 * Hook to manually refresh a token
 */
export const useRefreshToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tokenId: string) => tokenService.refreshToken(tokenId),
    onSuccess: () => {
      // Invalidate token list to refetch updated data
      queryClient.invalidateQueries({ queryKey: tokenKeys.lists() });
    },
  });
};

/**
 * Hook to delete a token
 */
export const useDeleteToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tokenId: string) => tokenService.deleteToken(tokenId),
    onSuccess: () => {
      // Invalidate token list to refetch
      queryClient.invalidateQueries({ queryKey: tokenKeys.lists() });
    },
  });
};

/**
 * Hook to update token, provider, and template codes together
 */
export const useUpdateTokenCodes = (tokenId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateTokenCodesRequest) => {
      if (!tokenId) {
        throw new Error('Token ID is missing');
      }

      await tokenService.updateTokenCodes(tokenId, data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tokenKeys.lists() });

      if (tokenId) {
        await queryClient.invalidateQueries({ queryKey: tokenKeys.detail(tokenId) });
      }
    },
  });
};
